import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'horizontal' | 'featured';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'featured') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group block relative overflow-hidden rounded-xl cursor-pointer"
      >
        <div className="aspect-[16/7] relative overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent"></div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6">
          <div className="flex items-center gap-2 mb-2">
            {article.isExclusive && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-accent-500 text-background-50">
                独家
              </span>
            )}
            {article.isHot && (
              <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-primary-500 text-background-50">
                热门
              </span>
            )}
            <span className="text-xs text-background-50/80">{article.category}</span>
          </div>
          <h2 className="text-lg md:text-xl font-bold font-heading text-background-50 leading-snug group-hover:text-accent-300 transition-colors line-clamp-2">
            {article.title}
          </h2>
          <div className="flex items-center gap-3 mt-2 text-xs text-background-50/70">
            <span>{article.publishDate}</span>
            <span>{article.readTime} 分钟阅读</span>
            <span>{article.viewCount.toLocaleString()} 次阅读</span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'horizontal') {
    return (
      <Link
        to={`/article/${article.slug}`}
        className="group flex gap-4 p-3 rounded-xl bg-background-50 hover:bg-primary-50/50 transition-colors cursor-pointer"
      >
        <div className="w-[140px] h-[95px] flex-shrink-0 rounded-lg overflow-hidden">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover object-top transition-transform duration-300 group-hover:scale-105"
          />
        </div>
        <div className="flex-1 min-w-0 flex flex-col justify-between">
          <div>
            <div className="flex items-center gap-1.5 mb-1">
              {article.isExclusive && (
                <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 whitespace-nowrap">
                  独家
                </span>
              )}
              <span className="text-xs text-secondary-500">{article.category}</span>
            </div>
            <h3 className="text-sm font-bold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug">
              {article.title}
            </h3>
          </div>
          <span className="text-xs text-secondary-400">{article.publishDate}</span>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.slug}`}
      className="group block rounded-xl overflow-hidden bg-background-50 border border-secondary-200/60 hover:border-primary-200 transition-all cursor-pointer"
    >
      <div className="aspect-[16/10] overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
        />
      </div>
      <div className="p-4">
        <div className="flex items-center gap-1.5 mb-2">
          {article.isExclusive && (
            <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-accent-100 text-accent-700 whitespace-nowrap">
              独家
            </span>
          )}
          <span className="text-xs text-secondary-500">{article.category}</span>
          <span className="text-xs text-secondary-400">{article.publishDate}</span>
        </div>
        <h3 className="text-sm font-bold font-heading text-foreground-900 group-hover:text-primary-600 transition-colors line-clamp-2 leading-snug mb-2">
          {article.title}
        </h3>
        <p className="text-xs text-secondary-500 line-clamp-2 leading-relaxed">
          {article.excerpt}
        </p>
        <div className="flex items-center gap-2 mt-3 text-xs text-secondary-400">
          <span><i className="ri-eye-line mr-1"></i>{article.viewCount.toLocaleString()}</span>
          <span><i className="ri-time-line mr-1"></i>{article.readTime}分钟</span>
        </div>
      </div>
    </Link>
  );
}